// src/connectors/constants.js
//
// Static provider/field catalogue for the Connections feature. Four
// families — LLM, Source Control, Work Item, CRM — each with a fixed
// provider list and a per-provider field schema the register form renders
// dynamically from.
//
// LLM's own provider catalogue lives in src/llmConnections/constants.js
// (imported by ConnectorsPage.jsx directly, not re-exported through
// PROVIDERS_BY_FAMILY below) — unlike the other three (a flat org+creator
// model: each user only ever sees the connections they personally
// registered, gated by connections:write_personal to register and
// connections:read to list — see
// application/shared/connections/queries/list_source_control_connections_handler.py's
// own docstring, and connections_controller.py's own comment on why
// registering isn't gated by connections:write, which also gates an
// org-wide LLM connection), LLM connections can be registered at four
// different levels (org/account/project/personal) with different
// permission requirements per level, so ConnectorsPage renders a different
// register modal/card for that tab (RegisterLlmConnectionModal/
// LlmConnectionCard) while still hosting it as one of this page's tabs.
//
// Field shape: { name, label, type: "text"|"password"|"select", required,
// placeholder?, helperText?, options? (for type:"select") }.
//
// This catalogue is built directly from the real backend's request/tester
// contracts (architecture_scaffold/presentation/shared/connections/schemas/,
// infrastructure/shared/connections/*_connection_tester.py) — every field
// here maps to something the backend actually reads, and every field the
// backend requires is collected. In particular: ServiceNow is a Source
// Control provider (SourceControlType.SERVICENOW), not Work Item — the
// backend has no ServiceNow work-item support at all.

export const CONNECTOR_FAMILIES = [
  { key: "llm", label: "LLM" },
  { key: "source_control", label: "Source Control" },
  { key: "work_item", label: "Work Item" },
  { key: "crm", label: "CRM" },
];

export const PROVIDERS_BY_FAMILY = {
  source_control: [
    {
      value: "github",
      label: "GitHub",
      initials: "GH",
      fields: [
        { name: "name", label: "Connection name", type: "text", required: true, placeholder: "e.g. Org GitHub" },
        { name: "repository", label: "Repository", type: "text", required: true, placeholder: "owner/repo" },
        { name: "token", label: "Personal access token", type: "password", required: true },
      ],
    },
    {
      value: "azure_devops",
      label: "Azure DevOps Repos",
      initials: "AD",
      fields: [
        { name: "name", label: "Connection name", type: "text", required: true, placeholder: "e.g. Org Azure Repos" },
        { name: "org", label: "Organization", type: "text", required: true, placeholder: "your-org" },
        { name: "project", label: "Project", type: "text", required: true, placeholder: "MyProject" },
        { name: "repository", label: "Repository", type: "text", required: true, placeholder: "MyRepo" },
        { name: "pat", label: "Personal access token", type: "password", required: true },
      ],
    },
    {
      value: "bitbucket",
      label: "Bitbucket",
      initials: "BB",
      fields: [
        { name: "name", label: "Connection name", type: "text", required: true, placeholder: "e.g. Team Bitbucket" },
        { name: "repository", label: "Repository", type: "text", required: true, placeholder: "workspace/repo_slug" },
        { name: "pat", label: "App password", type: "password", required: true },
      ],
    },
    {
      value: "servicenow",
      label: "ServiceNow",
      initials: "SN",
      fields: [
        { name: "name", label: "Connection name", type: "text", required: true, placeholder: "e.g. ServiceNow ITSM" },
        { name: "instanceUrl", label: "Instance URL", type: "text", required: true, placeholder: "https://yourinstance.service-now.com" },
        { name: "username", label: "Username", type: "text", required: true },
        { name: "password", label: "Password", type: "password", required: true },
      ],
    },
  ],

  work_item: [
    {
      value: "jira",
      label: "Jira",
      initials: "JR",
      fields: [
        { name: "name", label: "Connection name", type: "text", required: true, placeholder: "e.g. Jira Cloud" },
        { name: "siteUrl", label: "Site URL", type: "text", required: true, placeholder: "https://yourcompany.atlassian.net" },
        { name: "email", label: "Account email", type: "text", required: true },
        { name: "projectKey", label: "Project key", type: "text", required: true, placeholder: "PROJ" },
        { name: "apiToken", label: "API token", type: "password", required: true },
      ],
    },
    {
      value: "azure_devops",
      label: "Azure DevOps Boards",
      initials: "AD",
      fields: [
        { name: "name", label: "Connection name", type: "text", required: true, placeholder: "e.g. Org Boards" },
        { name: "org", label: "Organization", type: "text", required: true, placeholder: "your-org" },
        { name: "project", label: "Project", type: "text", required: true, placeholder: "MyProject" },
        { name: "pat", label: "Personal access token", type: "password", required: true },
      ],
    },
  ],

  crm: [
    {
      value: "salesforce",
      label: "Salesforce",
      initials: "SF",
      fields: [
        { name: "name", label: "Connection name", type: "text", required: true, placeholder: "e.g. Salesforce Production" },
        { name: "loginUrl", label: "Login URL", type: "text", required: true, placeholder: "https://login.salesforce.com" },
        { name: "username", label: "Username", type: "text", required: true },
        { name: "password", label: "Password", type: "password", required: true },
        { name: "securityToken", label: "Security token", type: "password", required: true },
      ],
    },
  ],
};

export function getProvider(family, providerValue) {
  return (PROVIDERS_BY_FAMILY[family] || []).find((p) => p.value === providerValue);
}
